import React, { useCallback, useEffect, useState } from 'react'
import { Control, UseFormSetValue } from 'react-hook-form'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { TextColors, Typography } from '@sureco/design-system'
import RHFInput from './form/RHFInput'
import RHFToggle from './form/RHFToggle'
import RHFCurrencyInput from './form/RHFCurrencyInput'
import type { PlanBenefitOverrideFormValues } from './planBenefitOverrideForm.schema'

export interface ChipDefinition {
  name: string
  label: string
  type: 'boolean' | 'number' | 'currency' | 'string'
}

export const PARTIAL_CHIPS: ChipDefinition[] = [
  {
    name: 'appliesToDeductible',
    label: 'Applies to deductible',
    type: 'boolean',
  },
  { name: 'coinsurance', label: 'Coinsurance', type: 'number' },
  { name: 'copayAmount', label: 'Copay amount ($)', type: 'currency' },
  { name: 'benefitDisplayValue', label: 'Display value', type: 'string' },
  { name: 'notes', label: 'Notes', type: 'string' },
]

interface Props {
  control: Control<PlanBenefitOverrideFormValues>
  setValue: UseFormSetValue<PlanBenefitOverrideFormValues>
  isReadOnly?: boolean
  initialSelected?: string[]
  onSelectionChange?: (selected: Set<string>) => void
}

export default function PartialFieldChipSelector({
  control,
  setValue,
  isReadOnly = false,
  initialSelected,
  onSelectionChange,
}: Props) {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialSelected ?? [])
  )
  const [hasInteracted, setHasInteracted] = useState(false)

  useEffect(() => {
    if (initialSelected) {
      setSelected(new Set(initialSelected))
    }
  }, [initialSelected])

  const addField = useCallback(
    (name: string, chip: ChipDefinition) => {
      setHasInteracted(true)
      setSelected((prev) => {
        const next = new Set(prev)
        next.add(name)
        onSelectionChange?.(next)
        return next
      })
      if (chip.type === 'boolean') {
        setValue(
          `params.${name}` as keyof PlanBenefitOverrideFormValues as any,
          false
        )
      }
    },
    [setValue, onSelectionChange]
  )

  const removeField = useCallback(
    (name: string) => {
      setHasInteracted(true)
      setSelected((prev) => {
        const next = new Set(prev)
        next.delete(name)
        onSelectionChange?.(next)
        return next
      })
      setValue(
        `params.${name}` as keyof PlanBenefitOverrideFormValues as any,
        undefined
      )
    },
    [setValue, onSelectionChange]
  )

  const selectedChips = PARTIAL_CHIPS.filter((c) => selected.has(c.name))
  const unselectedChips = PARTIAL_CHIPS.filter((c) => !selected.has(c.name))

  return (
    <div style={{ marginTop: 16 }}>
      {!isReadOnly && (
        <Typography className="chipSectionLabel">
          {unselectedChips.length > 0
            ? 'Select fields to override'
            : 'All fields selected'}
        </Typography>
      )}
      {!isReadOnly && unselectedChips.length > 0 && (
        <div className="partialChipRow">
          {unselectedChips.map((chip) => (
            <button
              key={chip.name}
              type="button"
              className="partialChip partialChipUnselected"
              onClick={() => addField(chip.name, chip)}
            >
              <PlusOutlined className="chipAddIcon" />
              {chip.label}
            </button>
          ))}
        </div>
      )}

      {selectedChips.length > 0 && (
        <div className="partialExpandedCards">
          {selectedChips.map((chip) => (
            <div key={chip.name} className="chipExpandedCard">
              <div className="chipExpandedCardHeader">
                <Typography className="chipFieldLabel">
                  {chip.label}
                  {chip.type !== 'boolean' && (
                    <span className="requiredMark">*</span>
                  )}
                </Typography>
                {!isReadOnly && (
                  <button
                    type="button"
                    className="chipRemoveBtn"
                    onClick={() => removeField(chip.name)}
                    aria-label={`Remove ${chip.label}`}
                  >
                    <CloseOutlined className="chipRemoveIcon" />
                  </button>
                )}
              </div>
              {chip.type === 'boolean' && (
                <RHFToggle
                  name={`params.${chip.name}`}
                  control={control}
                  disabled={isReadOnly}
                />
              )}
              {chip.type === 'number' && (
                <RHFInput
                  name={`params.${chip.name}`}
                  control={control}
                  placeholder="-1"
                  type="number"
                  min={-1}
                  disabled={isReadOnly}
                />
              )}
              {chip.type === 'currency' && (
                <RHFCurrencyInput
                  name={`params.${chip.name}`}
                  control={control}
                  placeholder="-1"
                  min={-1}
                  disabled={isReadOnly}
                />
              )}
              {chip.type === 'string' && (
                <RHFInput
                  name={`params.${chip.name}`}
                  control={control}
                  placeholder=""
                  disabled={isReadOnly}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {isReadOnly && selectedChips.length === 0 && (
        <Typography className="chipEmptyText">No fields selected.</Typography>
      )}

      {!isReadOnly && hasInteracted && selected.size === 0 && (
        <Typography color={TextColors.error} className="chipFieldLabel">
          Select at least one field to override.
        </Typography>
      )}
    </div>
  )
}
